import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ArrowLeftRight,
  FlipHorizontal,
  Gauge,
  Maximize,
  Repeat,
  Sparkles,
  ZoomIn,
} from "lucide-react";

interface SharedVideoGuideModalProps {
  open: boolean;
  onDismiss: () => void;
  canUseAbLoop: boolean;
}

const BASE_FEATURES = [
  {
    title: "Mirror mode",
    description: "Flip the video so the movement matches your perspective.",
    icon: FlipHorizontal,
  },
  {
    title: "L/R overlay",
    description: "Show left and right labels to make orientation easier to follow.",
    icon: ArrowLeftRight,
  },
  {
    title: "Speed control",
    description: "Slow down or speed up the video to practice at your pace.",
    icon: Gauge,
  },
  {
    title: "Pinch to zoom and pan",
    description: "Zoom in and move around the video to inspect movement details on mobile.",
    icon: ZoomIn,
  },
  {
    title: "Full screen",
    description: "Focus on the video with fewer distractions.",
    icon: Maximize,
  },
] as const;

const AB_LOOP_FEATURE = {
  title: "A/B looping",
  description: "Choose any section of the choreography and repeat it until it feels right.",
  icon: Repeat,
} as const;

export function SharedVideoGuideModal({
  open,
  onDismiss,
  canUseAbLoop,
}: SharedVideoGuideModalProps) {
  const features = canUseAbLoop
    ? [BASE_FEATURES[0], BASE_FEATURES[1], AB_LOOP_FEATURE, ...BASE_FEATURES.slice(2)]
    : BASE_FEATURES;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onDismiss(); }}>
      <DialogContent className="sm:max-w-md" data-testid="modal-shared-video-guide">
        <DialogHeader>
          <div className="mx-auto w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center mb-2">
            <Sparkles className="w-6 h-6 text-primary" />
          </div>
          <DialogTitle className="text-center">Practice smarter with DanceMirror</DialogTitle>
          <DialogDescription className="text-center">
            A quick guide to the tools in your shared video
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] mt-2">
          <ul className="space-y-4 pr-3">
            {features.map(({ title, description, icon: Icon }) => (
              <li key={title} className="flex gap-3">
                <div className="mt-0.5 w-7 h-7 rounded-full flex items-center justify-center shrink-0 bg-primary/15 text-primary">
                  <Icon className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-sm font-medium">{title}</p>
                  <p className="text-sm text-muted-foreground">{description}</p>
                </div>
              </li>
            ))}
          </ul>
        </ScrollArea>

        <Button className="w-full mt-4" onClick={onDismiss} data-testid="button-shared-video-guide-dismiss">
          Got it
        </Button>
      </DialogContent>
    </Dialog>
  );
}